// Accent Every (MDL-297, spec 131989506 §4) — option list and the captions that
// say what a setting means for the current meter and Div.
//
// `accent_every` is "bar" (accent the cycle that starts on the accent beat, once
// per bar: the original behaviour) or an N from ACCENT_EVERY_STEPS (accent every
// Nth cycle, counted from the last reset). Absent = "bar", so there is no
// migration. ACCENT_EVERY_STEPS mirrors the firmware's ACCENT_EVERY_VALUES
// (app/lib/midulate/effect.py), which is what it accepts.
//
// The knob does not offer 1. Accenting every cycle just makes the accent the
// normal waveform: with a positive accent it is identical to raising Max, with
// Mask it silences the effect, and only the negative accents and flipflop do
// anything Min/Max cannot. A hand-edited `accent_every: 1` still works and is
// still shown. The offered list is also the enum an incoming CC would sweep
// (MDL-418), so its order is a contract.

// What the firmware accepts.
export const ACCENT_EVERY_STEPS = [1, 2, 3, 4, 6, 8, 12];

// What the knob offers.
export const ACCENT_EVERY_OFFERED = [2, 3, 4, 6, 8, 12];

// The knob readout is narrow, so the label is just the setting. The meaning for
// the current meter and Div goes in the tooltip (accentEveryTip).
const option = n => ({ value: n, label: String(n) });
export const ACCENT_EVERY_OPTIONS = [
  { value: 'bar', label: 'Bar' },
  ...ACCENT_EVERY_OFFERED.map(option),
];

// The options for a knob currently set to `value`. A stored 1 (hand-edited, or
// from before the knob stopped offering it) stays selectable so the knob never
// shows a setting the device is not using.
export function accentEveryOptions(value) {
  return value === 1
    ? [ACCENT_EVERY_OPTIONS[0], option(1), ...ACCENT_EVERY_OPTIONS.slice(1)]
    : ACCENT_EVERY_OPTIONS;
}

// The device advertises `accent_every` in its capabilities; older firmware
// omits it, and the knob stays hidden (no mixed state).
export function isAccentEverySupported(capabilities) {
  const caps = capabilities?.capabilities ?? capabilities ?? {};
  return !!caps.accent_every;
}

// The Every knob shows once an accent is set, on a device that supports it.
// With no accent there is nothing to space out. The editors only render it in
// the clock-synced row, and only accent-capable types ever carry an accent, so
// neither is checked here.
export function isAccentEveryVisible(effect, capabilities) {
  return !!effect?.accent && effect.accent !== 'none'
    && isAccentEverySupported(capabilities);
}

// Absent, or anything that is not in the set, reads as Bar (as the firmware does).
export function accentEveryValue(effect) {
  const v = effect?.accent_every;
  return ACCENT_EVERY_STEPS.includes(v) ? v : 'bar';
}

// Write the choice in place. Bar is stored as an absent key, so a patch that
// never used the feature is unchanged and old firmware sees nothing new.
export function setAccentEvery(effect, value) {
  if (value === 'bar' || !ACCENT_EVERY_STEPS.includes(value)) {
    delete effect.accent_every;
  } else {
    effect.accent_every = value;
  }
}

// Cycle length in beats, as a fraction { n, d } (n/d beats), from the Div string.
//   "…b"  beat-anchored: "1/4b", "2/3b", "1b", "2b"
//   "1".."16" bar multiples: n bars
//   "1/16".."1/2" legacy bar fractions (firmware before note values)
// Returns null for anything unrecognised.
export function cycleBeats(rate, meter) {
  const s = String(rate ?? '1');
  let m = /^(\d+)(?:\/(\d+))?b$/.exec(s);
  if (m) return { n: Number(m[1]), d: Number(m[2] ?? 1) };
  m = /^(\d+)$/.exec(s);
  if (m) return { n: Number(m[1]) * meter, d: 1 };
  m = /^(\d+)\/(\d+)$/.exec(s);
  if (m) return { n: meter * Number(m[1]), d: Number(m[2]) };
  return null;
}

const gcd = (a, b) => (b === 0 ? a : gcd(b, a % b));

const FRACTIONS = { '1/2': '½', '1/3': '⅓', '2/3': '⅔', '1/4': '¼', '3/4': '¾' };

// "1 beat", "2 beats", "½ beat", "1½ beats" ...
function formatBeats(n, d) {
  const g = gcd(n, d);
  n /= g; d /= g;
  const whole = Math.floor(n / d);
  const rem = n - whole * d;
  const frac = rem ? (FRACTIONS[`${rem}/${d}`] ?? `${rem}/${d}`) : '';
  const text = `${whole || ''}${frac}` || '0';
  return `${text} ${whole === 0 && frac || (whole === 1 && !frac) ? 'beat' : 'beats'}`;
}

const barsText = k => (k === 1 ? 'every bar' : `every ${k} bars`);

// What a setting means for this meter and Div.
// Returns { text, repeatsBars } where text is "3 · rotating", "4 · every bar",
// "2 · every 2 beats" ..., and repeatsBars is how many bars a rotation takes to
// come round again (null when the accent is regular).
export function describeAccentEvery(value, rate, meter) {
  if (value === 'bar') {
    return { text: 'Bar · every bar (on the accent beat)', repeatsBars: null };
  }
  const N = value;
  const c = cycleBeats(rate, meter);
  if (!c) return { text: `${N} · every ${N} cycles`, repeatsBars: null };

  const barN = meter * c.d;                 // a bar, in units of 1/d beat
  const shortCycle = c.n <= barN;           // fits in a bar: restarts each bar

  if (shortCycle) {
    const divisible = barN % c.n === 0;
    const perBar = Math.ceil(barN / c.n);   // cycles in a bar (the last may be cut short)
    if (N % perBar === 0) {
      return { text: `${N} · ${barsText(N / perBar)}`, repeatsBars: null };
    }
    if (perBar % N === 0 && divisible) {
      const beats = formatBeats(N * c.n, c.d);
      return { text: `${N} · every ${beats === '1 beat' ? 'beat' : beats}`, repeatsBars: null };
    }
    if (N === 1) return { text: '1 · every cycle', repeatsBars: null };
    const repeats = N / gcd(N, perBar);
    return { text: `${N} · rotating`, repeatsBars: repeats };
  }

  // A cycle longer than a bar runs on the continuous beat count.
  const spanN = N * c.n;                    // N cycles, in units of 1/d beat
  if (spanN % barN === 0) {
    return { text: `${N} · ${barsText(spanN / barN)}`, repeatsBars: null };
  }
  let k = 1;
  while ((k * barN) % spanN !== 0 && k < 48) k++;
  return { text: `${N} · rotating`, repeatsBars: k };
}

// Tooltip for the Every knob, for the current setting.
export function accentEveryTip(value, rate, meter) {
  const { text, repeatsBars } = describeAccentEvery(value, rate, meter);
  const intro = 'Accent every Nth cycle instead of once per bar.';
  const repeat = repeatsBars
    ? ` The pattern comes round again after ${repeatsBars} ${repeatsBars === 1 ? 'bar' : 'bars'}.`
    : '';
  return `${intro} ${text}.${repeat}`;
}
